import { useState } from 'react';
import { Button, Card, Center, Group, Loader, Stack, Text, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useAuditEvents } from '@/api/audit';
import { errorMessage } from '@/api/errors';
import { useObject, useObjectSessions, useRenameObject, type DataObject } from '@/api/objects';
import { useProject } from '@/api/projects';
import { useProjectReports } from '@/api/reports';
import { useObjectRuns } from '@/api/runs';
import { EmptyState } from '@/components/EmptyState';
import { StaffBreadcrumbs } from '@/components/StaffBreadcrumbs';
import { InlineRename } from '@/components/InlineRename';
import { usePageTitle } from '@/components/usePageTitle';
import { DeleteObjectModal } from '@/features/objects/DeleteObjectModal';
import { ObjectIntakeActions } from '@/features/objects/ObjectIntakeActions';
import { UploadPanel } from '@/features/objects/UploadPanel';
import { objectName } from '@/features/objects/objectName';
import { FindingsList, type Finding } from '@/features/runs/FindingsList';
import { ReportsTable } from '@/features/reports/ReportsTable';
import { RunsTable } from '@/features/runs/RunsTable';
import { AttachUriModal } from '@/features/staff/AttachUriModal';
import { AuditTimeline } from '@/features/staff/AuditTimeline';
import { InternalComments } from '@/features/staff/InternalComments';
import { ObjectInternals } from '@/features/staff/ObjectInternals';
import { ObjectListingModal } from '@/features/staff/ObjectListingModal';
import { brand } from '@/theme/theme';

export const Route = createFileRoute('/_staff/staff/objects/$objectId')({ component: StaffObjectPage });

function StaffObjectPage() {
  const { t } = useTranslation('projects');
  const { t: tc } = useTranslation();
  const { t: tr } = useTranslation('runs');
  const { objectId } = Route.useParams();
  const object = useObject(objectId);
  const sessions = useObjectSessions(objectId);
  const audit = useAuditEvents({ subject_id: objectId });
  const runs = useObjectRuns(objectId);
  const project = useProject(object.data?.project);
  const reports = useProjectReports(object.data?.project);
  const rename = useRenameObject(objectId);
  const navigate = useNavigate();
  const [toDelete, setToDelete] = useState<DataObject | null>(null);
  const [attachOpen, setAttachOpen] = useState(false);
  usePageTitle(object.data ? objectName(object.data) : objectId);

  if (object.isPending) {
    return (
      <Center py="xl">
        <Loader color="forest" />
      </Center>
    );
  }
  if (object.isError) return <EmptyState title={errorMessage(object.error)} />;

  const o = object.data;
  // The upload surface and the attach offer exist only while nothing has landed yet.
  const awaitingData = o.origin === 'uploaded' && (o.state === 'pending' || o.state === 'uploading');
  const canAttach = o.origin === 'uploaded' && o.state === 'pending' && !o.deleted && project.data?.state === 'active';
  const objectReports = (reports.data ?? []).filter((r) => r.inputs.some((i) => i.object.public_id === objectId));

  return (
    <Stack gap="lg">
      <StaffBreadcrumbs projectId={o.project} current={objectName(o)} />
      <InlineRename
        order={1}
        value={objectName(object.data)}
        canEdit={!!object.data.can_rename}
        onSave={async (name) => {
          try {
            await rename.mutateAsync(name);
          } catch (err) {
            notifications.show({ color: 'red', message: errorMessage(err) });
            throw err;
          }
        }}
      />
      <Card>
        <Group justify="space-between" align="center">
          <Title order={3}>{t('staff.intakeActions')}</Title>
          <Group gap="xs">
            <ObjectIntakeActions object={object.data} projectActive={project.data?.state === 'active'} />
            {canAttach && (
              <Button size="xs" variant="default" onClick={() => setAttachOpen(true)}>
                {t('staff.attachUri')}
              </Button>
            )}
            {object.data.removable && (
              <Button color="red" variant="light" onClick={() => setToDelete(object.data)}>
                {tc('common.delete')}
              </Button>
            )}
          </Group>
        </Group>
        <Title order={4} mt="md" mb="xs">
          {tr('sections.findings')}
        </Title>
        <FindingsList findings={(object.data.findings ?? []) as Finding[]} />
      </Card>
      {awaitingData && !o.deleted && <UploadPanel object={o} />}
      <InternalComments subject={objectId} />
      <ObjectInternals object={object.data} sessions={sessions.data ?? []} />
      <Title order={2}>{tr('intake.runs')}</Title>
      {runs.isPending ? <Loader color="forest" /> : runs.data && runs.data.length > 0 ? <RunsTable runs={runs.data} hideObject /> : <Text fz={14} c={brand.textMuted}>{tr('intake.noRuns')}</Text>}
      <Title order={2}>{t('staff.objectReports')}</Title>
      {reports.isPending ? (
        <Loader color="forest" />
      ) : objectReports.length > 0 ? (
        <ReportsTable projectId={o.project} reports={objectReports} staff />
      ) : (
        <Text fz={14} c={brand.textMuted}>
          {t('staff.noObjectReports')}
        </Text>
      )}
      <Title order={2}>{t('staff.audit')}</Title>
      {audit.isPending ? <Loader color="forest" /> : <AuditTimeline events={audit.data ?? []} />}
      <ObjectListingModal object={o} />
      <AttachUriModal objectId={objectId} opened={attachOpen} onClose={() => setAttachOpen(false)} />
      <DeleteObjectModal
        object={toDelete}
        onClose={() => setToDelete(null)}
        onRemoved={() => void navigate({ to: '/staff/projects/$projectId', params: { projectId: object.data.project } })}
      />
    </Stack>
  );
}
